import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { precacheMedia } from '@/utils/cache'

const RobotScreen: React.FC = () => {
  const { t } = useLanguage()
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [lightboxImages, setLightboxImages] = useState<Array<{src: string, title: string}>>([])
  const [currentHeroMedia, setCurrentHeroMedia] = useState(0)
  const heroVideoRef = useRef<HTMLVideoElement>(null)

  const heroMediaItems = [
    {
      type: 'image',
      src: '/robot_main_photo.jpeg',
      duration: 5000,
      badge: t.robot.hero.advancedTech,
      title: t.robot.hero.techTitle,
      description: t.robot.hero.techDesc
    },
    {
      type: 'video',
      src: '/turbine_inside_video_robot.mp4',
      duration: 0,
      badge: t.robot.hero.liveInspection,
      title: t.robot.hero.inspectionTitle,
      description: t.robot.hero.inspectionDesc
    },
    {
      type: 'image',
      src: '/robot_operator_1.jpeg',
      duration: 5000,
      badge: t.robot.hero.expertTeam,
      title: t.robot.hero.teamTitle,
      description: t.robot.hero.teamDesc
    },
    {
      type: 'video',
      src: '/turbine_robot_inside_video.mp4',
      duration: 0,
      badge: t.robot.hero.precisionScanning,
      title: t.robot.hero.processTitle,
      description: t.robot.hero.processDesc
    },
    {
      type: 'image',
      src: '/control_room_2.jpeg',
      duration: 5000,
      badge: t.robot.hero.controlCenter,
      title: t.robot.hero.centerTitle,
      description: t.robot.hero.centerDesc
    },
    {
      type: 'image',
      src: '/robot_inside_blade_main.jpeg',
      duration: 5000,
      badge: t.robot.hero.damageAnalysis,
      title: t.robot.hero.bladeTitle,
      description: t.robot.hero.bladeDesc
    }
  ]

  useEffect(() => {
    const currentItem = heroMediaItems[currentHeroMedia]
    
    if (currentItem.type === 'video') {
      return
    }

    const timer = setTimeout(() => {
      setCurrentHeroMedia((prev) => (prev + 1) % heroMediaItems.length)
    }, currentItem.duration)

    return () => clearTimeout(timer)
  }, [currentHeroMedia])

  const handleHeroVideoEnd = () => {
    setCurrentHeroMedia((prev) => (prev + 1) % heroMediaItems.length)
  }

  const openImageLightbox = (images: Array<{src: string, title: string}>, index: number) => {
    setLightboxImages(images)
    setSelectedImageIndex(index)
  }

  const closeLightbox = () => {
    setSelectedImageIndex(null)
    setLightboxImages([])
  }

  const goToPrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  const goToNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < lightboxImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return

      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImageIndex, lightboxImages.length])

  const damageImages = [
    { src: '/damaged_area_1.jpeg', title: t.robot.damage.detection1 },
    { src: '/damaged_area_2.jpeg', title: t.robot.damage.detection2 },
    { src: '/damaged_area_3.jpeg', title: t.robot.damage.detection3 },
    { src: '/damaged_area_4.jpeg', title: t.robot.damage.detection4 }
  ]

  const images = [
    { src: '/robot_3.jpeg', title: t.robot.gallery.robotOp },
    { src: '/robot_4.jpeg', title: t.robot.gallery.equipment },
    { src: '/turbine_inside_2.jpeg', title: t.robot.gallery.interior },
    { src: '/turbine_upper_1.jpeg', title: t.robot.gallery.upper },
    { src: '/turbine_upper_2.jpeg', title: t.robot.gallery.platform },
    { src: '/turbines_1.jpeg', title: t.robot.gallery.general },
    { src: '/control_room_1.jpeg', title: t.robot.gallery.control },
    { src: '/robot_inside_blade.jpeg', title: t.robot.gallery.bladeInspection },
    { src: '/robot_inside_blade_main.jpeg', title: t.robot.gallery.detailedInspection },
    { src: '/robot_inside_blade_main_2.jpeg', title: t.robot.gallery.bladeScanning },
    { src: '/turbine_blade_1.jpeg', title: t.robot.gallery.blade },
    { src: '/turbine_blade_2.jpeg', title: t.robot.gallery.surface }
  ]

  // Preload all page images for instant display
  useEffect(() => {
    const allImageSrcs = [
      ...heroMediaItems.filter(item => item.type === 'image').map(item => item.src),
      ...damageImages.map(item => item.src),
      ...images.map(item => item.src)
    ]
    // Preload via browser Image objects for immediate rendering
    allImageSrcs.forEach(src => {
      const img = new Image()
      img.src = src
    })
    // Also ensure SW cache has videos
    const videoSrcs = heroMediaItems
      .filter(item => item.type === 'video')
      .map(item => item.src)
    precacheMedia(videoSrcs)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Hero Section with Media Slider */}
      <section className="relative h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
        {/* Media Background */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            {heroMediaItems[currentHeroMedia].type === 'video' ? (
              <motion.video
                key={`hero-video-${currentHeroMedia}`}
                ref={heroVideoRef}
                src={heroMediaItems[currentHeroMedia].src}
                autoPlay
                muted
                playsInline
                onEnded={handleHeroVideoEnd}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="w-full h-full object-cover"
              />
            ) : (
              <motion.img
                key={heroMediaItems[currentHeroMedia].src}
                src={heroMediaItems[currentHeroMedia].src}
                alt={t.ui.robotInspection}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentHeroMedia}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-4 border border-white/20">
                {heroMediaItems[currentHeroMedia]?.badge}
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 md:mb-6 drop-shadow-2xl">
                {heroMediaItems[currentHeroMedia]?.title}
              </h1>
              <p className="text-sm md:text-lg lg:text-xl text-white/90 leading-relaxed drop-shadow-lg">
                {heroMediaItems[currentHeroMedia]?.description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Media Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {heroMediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroMedia(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentHeroMedia 
                  ? 'w-8 bg-white' 
                  : 'w-1.5 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`${t.ui.goToMedia} ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Damage Detection Section */}
      <section className="py-8 md:py-10 lg:py-14 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #e2e8f0 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-red-100 rounded-full text-red-700 text-sm font-semibold mb-3">
                {t.robot.damage.badge}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                {t.robot.damage.title}
              </h2>
              <p className="text-base md:text-lg text-secondary-600 max-w-2xl mx-auto">
                {t.robot.damage.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {damageImages.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer border-2 border-red-200 hover:border-red-400"
                  onClick={() => openImageLightbox(damageImages, index)}
                >
                  <div className="relative aspect-[16/10]">
                    <img 
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Title */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-xs uppercase tracking-wider font-semibold text-red-400">
                          {t.robot.damage.detected}
                        </span>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-white">
                        {image.title}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-8 md:py-10 lg:py-14 bg-white relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-40 left-0 w-96 h-96 bg-primary-100/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-0 w-96 h-96 bg-secondary-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-secondary-100 rounded-full text-secondary-700 text-sm font-semibold mb-3">
                {t.robot.gallery.badge}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                {t.robot.gallery.title}
              </h2>
              <p className="text-base md:text-lg text-secondary-600 max-w-2xl mx-auto">
                {t.robot.gallery.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {images.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-primary-400"
                  onClick={() => openImageLightbox(images, index)}
                >
                  <img 
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Title on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-base md:text-lg font-semibold text-white">
                      {image.title}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Previous button */}
            {selectedImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Next button */}
            {selectedImageIndex < lightboxImages.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Image */}
            <div className="relative w-full h-full flex items-center justify-center p-4 md:p-8">
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImageIndex}
                  src={lightboxImages[selectedImageIndex]?.src}
                  alt={lightboxImages[selectedImageIndex]?.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </AnimatePresence>

              {/* Image info */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-6 py-3 rounded-full">
                <p className="text-white text-sm md:text-base font-medium">
                  {lightboxImages[selectedImageIndex]?.title}
                </p>
                <p className="text-white/60 text-xs text-center mt-1">
                  {selectedImageIndex + 1} / {lightboxImages.length}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RobotScreen
